import { supabase } from "@/supabaseClient";
import { OPS_ACTIVE_STATUSES } from "@/lib/blackride-constants";

function toNullIfEmpty(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
}

export function formatCurrency(value) {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat("en-SA", {
    style: "currency",
    currency: "SAR",
    maximumFractionDigits: 0,
  }).format(numeric);
}

export function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-SA", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value));
}

export function formatTime(value) {
  if (!value) return "—";
  const date = new Date(`2000-01-01T${String(value).slice(0, 5)}:00`);
  return new Intl.DateTimeFormat("en-SA", { hour: "numeric", minute: "2-digit" }).format(date);
}

export function buildDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const stamp = new Date(`${dateValue}T${String(timeValue).slice(0, 5)}:00`);
  if (Number.isNaN(stamp.getTime())) return null;
  return stamp;
}

export function computeBookingSchedule({ date, time, serviceType, durationHours, estimatedDurationMinutes }) {
  const start = buildDateTime(date, time);
  if (!start) {
    return { scheduled_start: null, scheduled_end: null };
  }

  const durationMinutes = serviceType === "hourly"
    ? Math.max(Number(durationHours || 4), 1) * 60
    : Math.max(Number(estimatedDurationMinutes || 120), 30);

  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  return {
    scheduled_start: start.toISOString(),
    scheduled_end: end.toISOString(),
  };
}

function handleSupabaseResult(result, fallback = null) {
  if (result.error) throw result.error;
  return result.data ?? fallback;
}

export async function fetchProfileById(userId) {
  if (!userId) return null;
  const result = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
  if (result.error) throw result.error;
  return result.data;
}

export async function signInWithEmail(email, password) {
  const result = await supabase.auth.signInWithPassword({ email, password });
  if (result.error) throw result.error;
  return result.data;
}

export async function signOutUser() {
  const result = await supabase.auth.signOut();
  if (result.error) throw result.error;
}

export async function getCurrentSessionBundle() {
  const sessionResult = await supabase.auth.getSession();
  if (sessionResult.error) throw sessionResult.error;
  const session = sessionResult.data.session;
  if (!session?.user) return { session: null, user: null, profile: null };
  const profile = await fetchProfileById(session.user.id);
  return { session, user: session.user, profile };
}

export async function fetchAdminSnapshot({ from, to }) {
  const bookingQuery = supabase
    .from("bookings")
    .select("*")
    .order("date", { ascending: false })
    .order("time", { ascending: false });

  const quoteQuery = supabase.from("quotes").select("*").order("created_at", { ascending: false });
  const vehicleQuery = supabase.from("vehicles").select("*").order("vehicle_name", { ascending: true });
  const driverQuery = supabase.from("drivers").select("*").order("name", { ascending: true });
  const expenseQuery = supabase.from("expenses").select("*").order("incurred_at", { ascending: false });

  if (from) {
    bookingQuery.gte("date", from);
    expenseQuery.gte("incurred_at", `${from}T00:00:00`);
  }
  if (to) {
    bookingQuery.lte("date", to);
    expenseQuery.lte("incurred_at", `${to}T23:59:59`);
  }

  const [bookingsResult, quotesResult, vehiclesResult, driversResult, expensesResult] = await Promise.all([
    bookingQuery,
    quoteQuery,
    vehicleQuery,
    driverQuery,
    expenseQuery,
  ]);

  return {
    bookings: handleSupabaseResult(bookingsResult, []),
    quotes: handleSupabaseResult(quotesResult, []),
    vehicles: handleSupabaseResult(vehiclesResult, []),
    drivers: handleSupabaseResult(driversResult, []),
    expenses: handleSupabaseResult(expensesResult, []),
  };
}

export async function fetchDriverRecord(authUserId) {
  const result = await supabase.from("drivers").select("*").eq("auth_user_id", authUserId).maybeSingle();
  if (result.error) throw result.error;
  return result.data;
}

export async function fetchDriverTrips(driverId) {
  if (!driverId) return [];
  const result = await supabase
    .from("bookings")
    .select("*")
    .eq("current_driver_id", driverId)
    .order("date", { ascending: true })
    .order("time", { ascending: true });
  return handleSupabaseResult(result, []);
}

export async function saveVehicle(payload) {
  const record = {
    vehicle_name: payload.vehicle_name?.trim(),
    model: payload.model?.trim(),
    year: toNullIfEmpty(payload.year),
    plate_number: toNullIfEmpty(payload.plate_number),
    category: payload.category?.trim(),
    seats: Number(payload.seats || 1),
    luggage_capacity: Number(payload.luggage_capacity || 0),
    status: payload.status || "active",
    availability: payload.availability || "available",
    notes: toNullIfEmpty(payload.notes),
  };

  const query = payload.id
    ? supabase.from("vehicles").update(record).eq("id", payload.id).select().single()
    : supabase.from("vehicles").insert(record).select().single();

  return handleSupabaseResult(await query);
}

export async function saveDriver(payload) {
  const languages = Array.isArray(payload.languages)
    ? payload.languages
    : String(payload.languages || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

  const record = {
    auth_user_id: toNullIfEmpty(payload.auth_user_id),
    name: payload.name?.trim(),
    phone: toNullIfEmpty(payload.phone),
    license_number: toNullIfEmpty(payload.license_number),
    languages,
    status: payload.status || "active",
    assigned_vehicle_id: toNullIfEmpty(payload.assigned_vehicle_id),
    availability: payload.availability || "available",
    notes: toNullIfEmpty(payload.notes),
  };

  const query = payload.id
    ? supabase.from("drivers").update(record).eq("id", payload.id).select().single()
    : supabase.from("drivers").insert(record).select().single();

  return handleSupabaseResult(await query);
}

export async function saveBooking(payload, createdBy) {
  const schedule = computeBookingSchedule({
    date: payload.date,
    time: payload.time,
    serviceType: payload.service_type,
    durationHours: payload.duration_hours,
    estimatedDurationMinutes: payload.estimated_duration_minutes,
  });

  const record = {
    customer_name: toNullIfEmpty(payload.customer_name),
    customer_phone: toNullIfEmpty(payload.customer_phone),
    customer_whatsapp: toNullIfEmpty(payload.customer_whatsapp),
    customer_email: toNullIfEmpty(payload.customer_email),
    pickup_location: payload.pickup_location?.trim(),
    drop_location: toNullIfEmpty(payload.drop_location),
    pickup_place_id: toNullIfEmpty(payload.pickup_place_id),
    drop_place_id: toNullIfEmpty(payload.drop_place_id),
    date: payload.date,
    time: String(payload.time || "").slice(0, 5),
    service_type: payload.service_type,
    vehicle_preference: toNullIfEmpty(payload.vehicle_preference),
    selected_vehicle_id: toNullIfEmpty(payload.selected_vehicle_id),
    passengers: Number(payload.passengers || 1),
    luggage: Number(payload.luggage || 0),
    duration_hours: payload.service_type === "hourly" ? Number(payload.duration_hours || 4) : null,
    estimated_duration_minutes: Number(payload.estimated_duration_minutes || 120),
    notes: toNullIfEmpty(payload.notes),
    booking_source: payload.booking_source || "admin_dashboard",
    status: payload.status || "new",
    payment_status: payload.payment_status || "pending",
    payment_method: toNullIfEmpty(payload.payment_method),
    amount_paid: Number(payload.amount_paid || 0),
    quoted_amount: toNullIfEmpty(payload.quoted_amount),
    current_driver_id: toNullIfEmpty(payload.current_driver_id),
    current_vehicle_id: toNullIfEmpty(payload.current_vehicle_id),
    created_by: createdBy || payload.created_by || null,
    ...schedule,
  };

  const query = payload.id
    ? supabase.from("bookings").update(record).eq("id", payload.id).select().single()
    : supabase.from("bookings").insert(record).select().single();

  return handleSupabaseResult(await query);
}

export async function createPublicBooking(payload) {
  const publicPayload = {
    ...payload,
    booking_source: payload.booking_source || "website",
    status: "new",
    current_driver_id: null,
    current_vehicle_id: null,
    payment_status: "pending",
    amount_paid: 0,
  };

  const schedule = computeBookingSchedule({
    date: publicPayload.date,
    time: publicPayload.time,
    serviceType: publicPayload.service_type,
    durationHours: publicPayload.duration_hours,
    estimatedDurationMinutes: publicPayload.estimated_duration_minutes,
  });

  const record = {
    customer_name: toNullIfEmpty(publicPayload.customer_name),
    customer_phone: toNullIfEmpty(publicPayload.customer_phone),
    customer_whatsapp: toNullIfEmpty(publicPayload.customer_whatsapp),
    customer_email: toNullIfEmpty(publicPayload.customer_email),
    pickup_location: publicPayload.pickup_location?.trim(),
    drop_location: toNullIfEmpty(publicPayload.drop_location),
    pickup_place_id: toNullIfEmpty(publicPayload.pickup_place_id),
    drop_place_id: toNullIfEmpty(publicPayload.drop_place_id),
    date: publicPayload.date,
    time: String(publicPayload.time || "").slice(0, 5),
    service_type: publicPayload.service_type,
    vehicle_preference: toNullIfEmpty(publicPayload.vehicle_preference),
    selected_vehicle_id: toNullIfEmpty(publicPayload.selected_vehicle_id),
    passengers: Number(publicPayload.passengers || 1),
    luggage: Number(publicPayload.luggage || 0),
    duration_hours: publicPayload.service_type === "hourly" ? Number(publicPayload.duration_hours || 4) : null,
    estimated_duration_minutes: Number(publicPayload.estimated_duration_minutes || 120),
    notes: toNullIfEmpty(publicPayload.notes),
    booking_source: publicPayload.booking_source,
    status: publicPayload.status,
    payment_status: publicPayload.payment_status,
    payment_method: toNullIfEmpty(publicPayload.payment_method),
    amount_paid: Number(publicPayload.amount_paid || 0),
    quoted_amount: toNullIfEmpty(publicPayload.quoted_amount),
    current_driver_id: null,
    current_vehicle_id: null,
    created_by: null,
    ...schedule,
  };

  const result = await supabase.from("bookings").insert(record);
  handleSupabaseResult(result, null);

  return {
    ...record,
    booking_reference: null,
  };
}
export async function saveQuote(payload, createdBy) {
  const baseFare = Number(payload.base_fare || 0);
  const extraCharges = Number(payload.extra_charges || 0);
  const discount = Number(payload.discount || 0);
  const vatAmount = Number(payload.vat_amount || 0);
  const totalAmount = baseFare + extraCharges - discount + vatAmount;

  const record = {
    booking_id: payload.booking_id,
    base_fare: baseFare,
    extra_charges: extraCharges,
    discount,
    vat_amount: vatAmount,
    total_amount: totalAmount,
    valid_until: toNullIfEmpty(payload.valid_until),
    notes: toNullIfEmpty(payload.notes),
    sent_at: payload.sent_at || null,
    created_by: createdBy || payload.created_by || null,
  };

  const existing = await supabase.from("quotes").select("id").eq("booking_id", payload.booking_id).maybeSingle();
  if (existing.error) throw existing.error;

  const query = existing.data?.id
    ? supabase.from("quotes").update(record).eq("id", existing.data.id).select().single()
    : supabase.from("quotes").insert(record).select().single();

  const quote = handleSupabaseResult(await query);

  await supabase
    .from("bookings")
    .update({ status: "quoted", quoted_amount: totalAmount })
    .eq("id", payload.booking_id);

  return quote;
}

export async function saveExpense(payload, createdBy) {
  const record = {
    booking_id: payload.booking_id,
    expense_type: payload.expense_type,
    amount: Number(payload.amount || 0),
    notes: toNullIfEmpty(payload.notes),
    incurred_at: payload.incurred_at || new Date().toISOString(),
    created_by: createdBy || payload.created_by || null,
  };

  const query = payload.id
    ? supabase.from("expenses").update(record).eq("id", payload.id).select().single()
    : supabase.from("expenses").insert(record).select().single();

  return handleSupabaseResult(await query);
}

export async function updateBookingPayment(payload) {
  const result = await supabase
    .from("bookings")
    .update({
      payment_status: payload.payment_status,
      payment_method: toNullIfEmpty(payload.payment_method),
      amount_paid: Number(payload.amount_paid || 0),
    })
    .eq("id", payload.id)
    .select()
    .single();

  return handleSupabaseResult(result);
}

export async function assignBookingResources({ booking, driverId, vehicleId }) {
  const conflictResult = await supabase.rpc("check_assignment_conflict", {
    p_booking_id: booking.id,
    p_driver_id: driverId || null,
    p_vehicle_id: vehicleId || null,
  });

  if (conflictResult.error) throw conflictResult.error;
  const conflict = Array.isArray(conflictResult.data) ? conflictResult.data[0] : conflictResult.data;
  if (conflict?.has_conflict) {
    return { ok: false, reason: conflict.conflict_reason };
  }

  const updateResult = await supabase
    .from("bookings")
    .update({
      current_driver_id: driverId || null,
      current_vehicle_id: vehicleId || null,
      status: "assigned",
    })
    .eq("id", booking.id)
    .select()
    .single();

  if (updateResult.error) throw updateResult.error;

  if (driverId) {
    await supabase.from("drivers").update({ availability: "assigned", assigned_vehicle_id: vehicleId || null }).eq("id", driverId);
  }
  if (vehicleId) {
    await supabase.from("vehicles").update({ availability: "assigned" }).eq("id", vehicleId);
  }

  return { ok: true, booking: updateResult.data };
}

export async function updateDriverTripStatus(booking, nextStatus) {
  const updateResult = await supabase
    .from("bookings")
    .update({ status: nextStatus })
    .eq("id", booking.id)
    .select()
    .single();
  if (updateResult.error) throw updateResult.error;

  if (nextStatus === "completed") {
    if (booking.current_driver_id) {
      await supabase.from("drivers").update({ availability: "available" }).eq("id", booking.current_driver_id);
    }
    if (booking.current_vehicle_id) {
      await supabase.from("vehicles").update({ availability: "available" }).eq("id", booking.current_vehicle_id);
    }
  }

  return updateResult.data;
}

export function buildQuoteWhatsappLink({ booking, quote }) {
  const message = [
    "Hello from BlackRide.",
    "",
    `Quote for booking ${booking.booking_reference}`,
    `Service: ${booking.service_type === "hourly" ? "Hourly Booking" : "Drop off"}`,
    `Pickup: ${booking.pickup_location}`,
    booking.drop_location ? `Drop-off: ${booking.drop_location}` : null,
    `Date: ${booking.date}`,
    `Time: ${String(booking.time || "").slice(0, 5)}`,
    `Vehicle preference: ${booking.vehicle_preference || "Any premium vehicle"}`,
    `Base fare: SAR ${Number(quote.base_fare || 0).toFixed(2)}`,
    Number(quote.extra_charges || 0) ? `Extra charges: SAR ${Number(quote.extra_charges).toFixed(2)}` : null,
    Number(quote.discount || 0) ? `Discount: SAR ${Number(quote.discount).toFixed(2)}` : null,
    Number(quote.vat_amount || 0) ? `VAT: SAR ${Number(quote.vat_amount).toFixed(2)}` : null,
    `Total amount: SAR ${Number(quote.total_amount || 0).toFixed(2)}`,
    quote.valid_until ? `Valid until: ${formatDate(quote.valid_until)}` : null,
    quote.notes ? `Notes: ${quote.notes}` : null,
    "",
    "Please reply on WhatsApp to confirm this quote.",
  ].filter(Boolean).join("\n");

  const phone = String(booking.customer_whatsapp || booking.customer_phone || "").replace(/[^0-9]/g, "");
  if (!phone) return "";
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export function buildBookingWhatsappLink({ booking }) {
  const phone = String(booking.customer_whatsapp || booking.customer_phone || "").replace(/[^0-9]/g, "");
  if (!phone) return "";
  const message = [
    "Hello from BlackRide.",
    "",
    `Your booking request ${booking.booking_reference} has been received.`,
    `Pickup: ${booking.pickup_location}`,
    booking.drop_location ? `Drop-off: ${booking.drop_location}` : null,
    `Date: ${booking.date}`,
    `Time: ${String(booking.time || "").slice(0, 5)}`,
    "We will review your request and confirm availability shortly.",
  ].filter(Boolean).join("\n");
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export function buildDepositRequestWhatsappLink({ booking, paymentLinkUrl, depositAmount }) {
  const phone = String(booking.customer_whatsapp || booking.customer_phone || "").replace(/[^0-9]/g, "");
  if (!phone || !paymentLinkUrl) return "";

  const totalAmount = Number(booking.quoted_amount || booking.quote?.total_amount || 0);
  const deposit = Number(depositAmount ?? totalAmount * 0.5);

  const message = [
    "Hello from BlackRide.",
    "",
    `Your trip ${booking.booking_reference} is ready to secure.`,
    `Pickup: ${booking.pickup_location}`,
    booking.drop_location ? `Drop-off: ${booking.drop_location}` : null,
    `Date: ${booking.date}`,
    `Time: ${String(booking.time || "").slice(0, 5)}`,
    totalAmount ? `Booking total: SAR ${totalAmount.toFixed(2)}` : null,
    `Required deposit (50%): SAR ${deposit.toFixed(2)}`,
    `Payment link: ${paymentLinkUrl}`,
    "Please complete the deposit to confirm dispatch and chauffeur allocation.",
  ].filter(Boolean).join("\n");

  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}


function createTrackingToken() {
  const raw = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  return String(raw).replace(/[^a-zA-Z0-9]/g, "").slice(0, 24).toLowerCase();
}

export async function ensureBookingTrackingToken(booking) {
  const token = booking.tracking_token || createTrackingToken();
  const scheduledStart = booking.scheduled_start ? new Date(booking.scheduled_start) : buildDateTime(booking.date, booking.time);
  const trackingEnabledAt = scheduledStart ? new Date(scheduledStart.getTime() - 30 * 60 * 1000).toISOString() : null;

  const result = await supabase
    .from("bookings")
    .update({
      tracking_token: token,
      tracking_enabled_at: trackingEnabledAt,
      tracking_shared_at: new Date().toISOString(),
    })
    .eq("id", booking.id)
    .select()
    .single();

  return handleSupabaseResult(result);
}

export function buildTrackingWhatsappLink({ booking, trackingUrl }) {
  const phone = String(booking.customer_whatsapp || booking.customer_phone || "").replace(/[^0-9]/g, "");
  if (!phone || !trackingUrl) return "";

  const message = [
    "Hello from BlackRide.",
    "",
    `Your chauffeur tracking link for trip ${booking.booking_reference} is ready.`,
    `Pickup: ${booking.pickup_location}`,
    booking.drop_location ? `Drop-off: ${booking.drop_location}` : null,
    `Date: ${booking.date}`,
    `Pickup time: ${String(booking.time || "").slice(0, 5)}`,
    "Live chauffeur location becomes visible 30 minutes before pickup.",
    `Track here: ${trackingUrl}`,
  ].filter(Boolean).join("\n");

  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export async function fetchGuestTrackingByToken(token) {
  if (!token) return null;
  const result = await supabase.rpc("fetch_guest_tracking", { p_token: token });
  if (result.error) throw result.error;
  const rows = Array.isArray(result.data) ? result.data : [];
  return rows[0] || null;
}

export async function publishDriverLocation({ bookingId, driverId, latitude, longitude, accuracyMeters, heading, speedKmh }) {
  const result = await supabase.from("driver_location_updates").insert({
    booking_id: bookingId,
    driver_id: driverId,
    latitude,
    longitude,
    accuracy_meters: accuracyMeters ?? null,
    heading: heading ?? null,
    speed_kmh: speedKmh ?? null,
  });

  if (result.error) throw result.error;
  return true;
}

export function createLookupMap(items) {
  return new Map((items || []).map((item) => [item.id, item]));
}

export function calculateMetrics({ bookings = [], quotes = [], vehicles = [], drivers = [], expenses = [], from, to }) {
  const quoteByBooking = createLookupMap(quotes.map((quote) => ({ ...quote, id: quote.booking_id })));
  const expenseByBooking = new Map();
  expenses.forEach((expense) => {
    const current = expenseByBooking.get(expense.booking_id) || 0;
    expenseByBooking.set(expense.booking_id, current + Number(expense.amount || 0));
  });

  const bookingsInScope = bookings.filter((booking) => {
    if (!from && !to) return true;
    const value = booking.date;
    if (from && value < from) return false;
    if (to && value > to) return false;
    return true;
  });

  const totalRevenue = bookingsInScope.reduce((sum, booking) => {
    const quote = quoteByBooking.get(booking.id);
    return sum + Number(booking.quoted_amount ?? quote?.total_amount ?? 0);
  }, 0);

  const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
  const completedTrips = bookingsInScope.filter((booking) => booking.status === "completed").length;
  const pendingPayments = bookingsInScope.reduce((sum, booking) => {
    const outstanding = Math.max(Number(booking.quoted_amount || 0) - Number(booking.amount_paid || 0), 0);
    return booking.payment_status === "paid" ? sum : sum + outstanding;
  }, 0);

  const totalBookedMinutes = bookingsInScope
    .filter((booking) => booking.status !== "cancelled")
    .reduce((sum, booking) => sum + Number(booking.estimated_duration_minutes || (booking.duration_hours || 0) * 60 || 120), 0);

  const startDate = from ? new Date(`${from}T00:00:00`) : (bookingsInScope[bookingsInScope.length - 1]?.date ? new Date(`${bookingsInScope[bookingsInScope.length - 1].date}T00:00:00`) : new Date());
  const endDate = to ? new Date(`${to}T23:59:59`) : (bookingsInScope[0]?.date ? new Date(`${bookingsInScope[0].date}T23:59:59`) : new Date());
  const daySpan = Math.max(Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1, 1);

  const vehicleOccupancy = vehicles.length ? Math.min(100, (totalBookedMinutes / (vehicles.length * daySpan * 24 * 60)) * 100) : 0;
  const driverUtilization = drivers.length ? Math.min(100, (totalBookedMinutes / (drivers.length * daySpan * 24 * 60)) * 100) : 0;

  const revenuePerVehicle = vehicles.map((vehicle) => ({
    id: vehicle.id,
    label: `${vehicle.vehicle_name} ${vehicle.model}`,
    revenue: bookingsInScope
      .filter((booking) => booking.current_vehicle_id === vehicle.id)
      .reduce((sum, booking) => sum + Number(booking.quoted_amount || quoteByBooking.get(booking.id)?.total_amount || 0), 0),
  })).sort((a, b) => b.revenue - a.revenue);

  const revenuePerDriver = drivers.map((driver) => ({
    id: driver.id,
    label: driver.name,
    revenue: bookingsInScope
      .filter((booking) => booking.current_driver_id === driver.id)
      .reduce((sum, booking) => sum + Number(booking.quoted_amount || quoteByBooking.get(booking.id)?.total_amount || 0), 0),
  })).sort((a, b) => b.revenue - a.revenue);

  const quotedBookings = bookingsInScope.filter((booking) => booking.status !== "new").length;
  const convertedBookings = bookingsInScope.filter((booking) => ["confirmed", "assigned", "ongoing", "completed"].includes(booking.status)).length;
  const quoteConversionRate = quotedBookings ? (convertedBookings / quotedBookings) * 100 : 0;

  return {
    totalBookings: bookingsInScope.length,
    totalRevenue,
    totalExpenses,
    pendingPayments,
    completedTrips,
    vehicleOccupancy,
    driverUtilization,
    revenuePerVehicle,
    revenuePerDriver,
    quoteConversionRate,
    bookingStatusBreakdown: bookingsInScope.reduce((acc, booking) => {
      acc[booking.status] = (acc[booking.status] || 0) + 1;
      return acc;
    }, {}),
    quoteByBooking,
    expenseByBooking,
    activeOperationsCount: bookingsInScope.filter((booking) => OPS_ACTIVE_STATUSES.includes(booking.status)).length,
  };
}
